import _ from 'lodash';
import { type PortalSettings, type ItemCommentsFieldValue, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { type ItemCommentsFieldDescriptor, type SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Tooltip, Icon, Toggle, Text } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { Dropdown } from '@vibe/core';
import { Forum as ForumIcon, LearnMore as LearnMoreIcon, Alert, ExternalPage } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { monday } from '../../services/monday';
import { ExternalLink } from '../ExternalLink';

const FILTER_MODE_OPTIONS = [
  {
    value: 'only-client',
    label: 'Clients will only see comments they made or that were addressed to them',
    endElement: {
      type: 'suffix',
      value: 'Recommended',
    },
  },
  {
    value: 'all-clients',
    label: 'Clients will see all comments made by or addressed to any client',
    startElement: {
      type: 'icon',
      value: Alert,
    },
  },
  {
    value: 'all',
    label: 'Clients will see all comments – including internal team comments',
    startElement: {
      type: 'icon',
      value: Alert,
    },
  },
];

const DEFAULT_FILTER_MODE = 'only-client';

type ItemCommentsFieldProps = {
  value: ItemCommentsFieldValue | undefined;
  fieldDescriptor: ItemCommentsFieldDescriptor;
  portalSettings: PortalSettings;
  sectionDescriptor: SectionDescriptor;
  sectionFieldValues: FieldValues;
  onChange: (value: ItemCommentsFieldValue | undefined) => void;
  clientBoardSubitemsBoardId: number | null;
};

export function ItemCommentsField({
  value,
  fieldDescriptor,
  portalSettings,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  onChange,
}: ItemCommentsFieldProps) {
  const { board, isLoading, boardId } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board) {
    return null;
  }

  const selectedFilterMode = value?.filterMode || DEFAULT_FILTER_MODE;
  const selectedFilterModeOption = FILTER_MODE_OPTIONS.find((option) => option.value === selectedFilterMode);

  return (
    <Box
      border={true}
      borderColor="layoutBorderColor"
      className="section__field section__field--itemcomments section__field--nomargin item--actions--wrapper"
    >
      <Box backgroundColor={!!value?.enabled ? 'greyBackgroundColor' : 'greyBackgroundColor'}>
        <Box padding="medium">
          <Flex
            gap={24}
            style={{
              height: '30px',
            }}
          >
            <Flex gap={8}>
              <Icon icon={ForumIcon} size={20} style={{ marginRight: '2px', transform: 'translate3d(1px,0,0)' }} />
              <Text type="text2" weight="bold" align="start" ellipsis={true}>
                Item comments
              </Text>
              <div>
                <Tooltip
                  title="What's this?"
                  content="Allow clients to comment on board items. Click to learn more."
                  moveBy={{ main: 5, secondary: -15 }}
                  position="right"
                  showOnDialogEnter={false}
                >
                  <div
                    onClick={() => {
                      monday.execute('openLinkInTab', {
                        url: 'https://getgorilla.app/products/client-portal/docs/user-guides/item-comments',
                      });
                    }}
                    style={{
                      cursor: 'pointer',
                    }}
                  >
                    <Icon icon={LearnMoreIcon} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                  </div>
                </Tooltip>
              </div>
            </Flex>
            <Box style={{ marginLeft: 'auto' }}>
              <Flex
                justify="start"
                gap={10}
                style={{
                  flex: 1,
                }}
              >
                <Box
                  style={{
                    marginRight: 'auto',
                  }}
                >
                  <Flex style={{ margin: '0 -8px' }}>
                    <Toggle
                      offOverrideText=""
                      onOverrideText=""
                      size="small"
                      isSelected={!!value?.enabled}
                      onChange={(isSelected) => {
                        onChange({ ...(value || {}), enabled: isSelected });
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Flex>
          {value?.enabled ? (
            <Box
              marginTop="medium"
              style={{
                overflow: 'visible',
              }}
            >
              <Flex gap={12} direction="column" justify="start" align="start">
                <Dropdown
                  placeholder="Filter mode"
                  size="small"
                  clearable={false}
                  searchable={false}
                  value={dropdownValue(selectedFilterModeOption)}
                  options={FILTER_MODE_OPTIONS}
                  onChange={(newFilterModeOption: any) => {
                    if (newFilterModeOption) {
                      onChange({ ...(value || {}), filterMode: newFilterModeOption.value });
                    }
                  }}
                  className="custom_dropdown_start-element"
                />

                {selectedFilterMode === 'all' ? (
                  <Flex gap={8} style={{ width: '100%' }}>
                    <Text type="text3" color="secondary" align="start" ellipsis={true} style={{ maxWidth: 'calc(100% - 70px)' }}>
                      Warning: This option exposes internal item updates to clients and has limited support for activity logs and comment
                      automations.
                    </Text>
                    <Text type="text3" color="secondary" align="start" ellipsis={false} style={{ whiteSpace: 'nowrap' }}>
                      <ExternalLink
                        href="https://getgorilla.app/products/client-portal/docs/user-guides/item-comments#anchor-7189"
                        style={{ display: 'inline-block' }}
                      >
                        Learn more
                      </ExternalLink>
                    </Text>
                  </Flex>
                ) : null}

                {selectedFilterMode === 'all-clients' ? (
                  <Flex gap={8} style={{ width: '100%' }}>
                    <Text type="text3" color="secondary" align="start" ellipsis={true} style={{ maxWidth: 'calc(100% - 70px)' }}>
                      Warning: This option exposes comments across all clients and has limited support for activity logs and comment
                      automations.
                    </Text>
                    <Text type="text3" color="secondary" align="start" ellipsis={false} style={{ whiteSpace: 'nowrap' }}>
                      <ExternalLink
                        href="https://getgorilla.app/products/client-portal/docs/user-guides/item-comments#anchor-7189"
                        style={{ display: 'inline-block' }}
                      >
                        Learn more
                      </ExternalLink>
                    </Text>
                  </Flex>
                ) : null}
              </Flex>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
